import {FC} from 'react'
import {useLang} from './Metronici18n'
import {IntlProvider} from 'react-intl'
import '@formatjs/intl-relativetimeformat/polyfill'
import '@formatjs/intl-relativetimeformat/locale-data/en'
import '@formatjs/intl-relativetimeformat/locale-data/de'
import '@formatjs/intl-relativetimeformat/locale-data/es'
import '@formatjs/intl-relativetimeformat/locale-data/fr'
import '@formatjs/intl-relativetimeformat/locale-data/ja'
import '@formatjs/intl-relativetimeformat/locale-data/zh'

import deMessages from './messages/de.json'
import enMessages from './messages/en.json'
import esMessages from './messages/es.json'
import frMessages from './messages/fr.json'
import jaMessages from './messages/ja.json'
import zhMessages from './messages/zh.json'
import {WithChildren} from '../helpers'

// Utility function to flatten nested objects
const flattenMessages = (nestedMessages: any, prefix = '') => {
  return Object.keys(nestedMessages).reduce((messages: any, key) => {
    const value = nestedMessages[key]
    const prefixedKey = prefix ? `${prefix}.${key}` : key

    if (typeof value === 'string') {
      messages[prefixedKey] = value
    } else {
      Object.assign(messages, flattenMessages(value, prefixedKey))
    }

    return messages
  }, {})
}

// Flatten all messages
const allMessages = {
  de: flattenMessages(deMessages),
  en: flattenMessages(enMessages),
  es: flattenMessages(esMessages),
  fr: flattenMessages(frMessages),
  ja: flattenMessages(jaMessages),
  zh: flattenMessages(zhMessages),
}

const mergeWithFallback = (
  primary: Record<string, string>,
  ...fallbacks: Record<string, string>[]
): Record<string, string> => {
  const merged = { ...primary }
  for (const fallback of fallbacks) {
    for (const [key, value] of Object.entries(fallback)) {
      if (merged[key] === undefined) {
        merged[key] = value
      }
    }
  }
  return merged
}

const I18nProvider: FC<WithChildren> = ({children}) => {
  const locale = useLang()
  const en = allMessages.en
  const fr = allMessages.fr
  const localeMessages = allMessages[locale] || en
  const messages =
    locale === 'en'
      ? en
      : locale === 'fr'
        ? fr
        : mergeWithFallback(localeMessages, en, fr)

  return (
    <IntlProvider locale={locale} messages={messages}>
      {children}
    </IntlProvider>
  )
}

export {I18nProvider}
